import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Search, Package } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { showSuccess, showError } from "@/utils/toastHelpers";

export default function ReceiveStockForm({ onClose }) {
  const queryClient = useQueryClient();

  const [medicineSearch, setMedicineSearch] = useState("");
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [showMedicineSearch, setShowMedicineSearch] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      quantity: "",
      batchNumber: "",
      expiryDate: "",
      unitCost: "",
      supplier: "",
      purchaseOrderNumber: "",
      storageLocation: "",
      notes: "",
    },
  });

  const watchQuantity = watch("quantity");
  const watchUnitCost = watch("unitCost");

  // Search medicines
  const { data: searchResults = [] } = useQuery({
    queryKey: ["medicineSearch", medicineSearch],
    queryFn: () =>
      apiClient.get(
        `${ENDPOINTS.MEDICINES.SEARCH}?q=${encodeURIComponent(medicineSearch)}&limit=10`
      ),
    enabled: medicineSearch.length >= 2,
  });

  // Receive stock mutation
  const receiveStock = useMutation({
    mutationFn: (data) =>
      apiClient.post(ENDPOINTS.INVENTORY.RECEIVE_STOCK, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventoryTransactions"]);
      queryClient.invalidateQueries(["lowStockAlerts"]);
      showSuccess("Stock received successfully");
      onClose();
    },
    onError: (error) => {
      console.error("Failed to receive stock:", error);
      showError(
        "Failed to receive stock",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  const onSubmit = async (data) => {
    if (!selectedMedicine) {
      showError("Please select a medicine");
      return;
    }

    await receiveStock.mutateAsync({
      medicineId: selectedMedicine.id,
      quantity: parseInt(data.quantity),
      batchNumber: data.batchNumber,
      expiryDate: data.expiryDate,
      unitCost: parseFloat(data.unitCost),
      supplier: data.supplier,
      purchaseOrderNumber: data.purchaseOrderNumber,
      storageLocation: data.storageLocation,
      notes: data.notes,
    });
  };

  const handleSelectMedicine = (medicine) => {
    setSelectedMedicine(medicine);
    setShowMedicineSearch(false);
    setMedicineSearch("");
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Receive Stock</h2>
            <p className="text-sm text-slate-600 mt-1">
              Log incoming medicine shipment
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Medicine Selection */}
          <div>
            <Label className="mb-1">
              Medicine <span className="text-red-500">*</span>
            </Label>
            {selectedMedicine ? (
              <div className="flex items-center gap-3 p-3 border border-green-200 bg-green-50 rounded-md">
                <Package className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {selectedMedicine.company} {selectedMedicine.product}
                  </p>
                  <p className="text-sm text-slate-600">
                    {selectedMedicine.strength} {selectedMedicine.dosageForm} -{" "}
                    {selectedMedicine.packSize}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMedicine(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    value={medicineSearch}
                    onChange={(e) => {
                      setMedicineSearch(e.target.value);
                      setShowMedicineSearch(true);
                    }}
                    placeholder="Search for medicine..."
                    className="pl-10"
                    onFocus={() => setShowMedicineSearch(true)}
                  />
                </div>

                {/* Search Results */}
                {showMedicineSearch && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((medicine) => (
                      <button
                        key={medicine.id}
                        type="button"
                        onClick={() => handleSelectMedicine(medicine)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      >
                        <p className="font-medium text-slate-900">
                          {medicine.company} {medicine.product}
                        </p>
                        <p className="text-sm text-slate-600">
                          {medicine.strength} {medicine.dosageForm} -{" "}
                          {medicine.packSize}
                          {medicine.genericName && ` (${medicine.genericName})`}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {showMedicineSearch &&
                  medicineSearch.length >= 2 &&
                  searchResults.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-4 text-center text-slate-600">
                      No medicines found. Try a different search term.
                    </div>
                  )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quantity */}
            <div>
              <Label htmlFor="quantity" className="mb-1">
                Quantity Received <span className="text-red-500">*</span>
              </Label>
              <Input
                id="quantity"
                type="number"
                placeholder="e.g., 100"
                min="1"
                step="1"
                {...register("quantity", {
                  required: "Quantity is required",
                  min: { value: 1, message: "Quantity must be at least 1" },
                  validate: (value) => {
                    const num = Number(value);
                    if (!Number.isInteger(num)) {
                      return "Quantity must be a whole number";
                    }
                    return true;
                  },
                })}
              />
              {errors.quantity && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.quantity.message}
                </p>
              )}
            </div>

            {/* Batch Number */}
            <div>
              <Label htmlFor="batchNumber" className="mb-1">
                Batch Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="batchNumber"
                placeholder="e.g., B12345"
                {...register("batchNumber", {
                  required: "Batch number is required",
                  validate: (value) => {
                    if (!value || !value.trim()) {
                      return "Batch number is required";
                    }
                    const alphanumericPattern = /^[a-zA-Z0-9-]+$/;
                    if (!alphanumericPattern.test(value.trim())) {
                      return "Batch number must be alphanumeric (letters, numbers, and hyphens only)";
                    }
                    return true;
                  },
                })}
              />
              {errors.batchNumber && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.batchNumber.message}
                </p>
              )}
            </div>

            {/* Expiry Date */}
            <div>
              <Label htmlFor="expiryDate" className="mb-1">
                Expiry Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="expiryDate"
                type="date"
                min={new Date().toISOString().split("T")[0]}
                {...register("expiryDate", {
                  required: "Expiry date is required",
                  validate: (value) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const expiryDate = new Date(value);
                    return expiryDate >= today || "Expiry date cannot be in the past";
                  },
                })}
              />
              {errors.expiryDate && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.expiryDate.message}
                </p>
              )}
            </div>

            {/* Unit Cost */}
            <div>
              <Label htmlFor="unitCost" className="mb-1">
                Unit Cost (ZAR) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="unitCost"
                type="number"
                step="0.01"
                placeholder="e.g., 15.50"
                min="0"
                {...register("unitCost", {
                  required: "Unit cost is required",
                  min: { value: 0, message: "Unit cost cannot be negative" },
                })}
              />
              {errors.unitCost && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.unitCost.message}
                </p>
              )}
            </div>

            {/* Supplier */}
            <div>
              <Label htmlFor="supplier" className="mb-1">
                Supplier <span className="text-red-500">*</span>
              </Label>
              <Input
                id="supplier"
                placeholder="e.g., ABC Pharmaceuticals"
                {...register("supplier", {
                  required: "Supplier is required",
                })}
              />
              {errors.supplier && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.supplier.message}
                </p>
              )}
            </div>

            {/* Purchase Order Number */}
            <div>
              <Label htmlFor="purchaseOrderNumber" className="mb-1">
                Purchase Order #
              </Label>
              <Input
                id="purchaseOrderNumber"
                placeholder="e.g., PO-2024-001"
                {...register("purchaseOrderNumber")}
              />
            </div>

            {/* Storage Location */}
            <div className="md:col-span-2">
              <Label htmlFor="storageLocation" className="mb-1">
                Storage Location
              </Label>
              <Input
                id="storageLocation"
                placeholder="e.g., Refrigerator A, Shelf 3"
                {...register("storageLocation")}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="mb-1">
              Notes
            </Label>
            <Textarea
              id="notes"
              rows="3"
              placeholder="Additional notes about this shipment..."
              {...register("notes")}
            />
          </div>

          {/* Summary */}
          {selectedMedicine && watchQuantity && watchUnitCost && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">
                Shipment Summary
              </h3>
              <div className="text-sm text-blue-800 space-y-1">
                <p>
                  <span className="font-medium">Medicine:</span>{" "}
                  {selectedMedicine.company} {selectedMedicine.product}{" "}
                  {selectedMedicine.strength}
                </p>
                <p>
                  <span className="font-medium">Quantity:</span>{" "}
                  {watchQuantity} packs
                </p>
                <p>
                  <span className="font-medium">Total Cost:</span> R
                  {(
                    parseFloat(watchQuantity) * parseFloat(watchUnitCost) || 0
                  ).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={receiveStock.isPending}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {receiveStock.isPending ? "Processing..." : "Receive Stock"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
